<script setup lang="ts">
  import type { ISection, ISectionChildItem } from '@/components/main-header/types'
  import type { IBlogEntry } from '@/server/api/blog-posts.get'

  const props = defineProps<{ activeSection?: ISection | null }>()

  const emit = defineEmits(['close'])

  const { locale } = useI18n()

  const blogEntries = ref<IBlogEntry[]>([])
  async function getBlogEntries() {
    if (!props.activeSection?.hasBlogBlock || blogEntries.value.length) {
      return
    }

    blogEntries.value = await $fetch<IBlogEntry[]>('/api/blog-posts', { method: 'GET', query: { lang: locale.value } })
  }

  const globalSettingsStore = useGlobalSettingsStore()

  watch(
    () => props.activeSection,
    async () => {
      await getBlogEntries()
    }
  )

  onUnmounted(() => emit('close'))

  const route = useRoute()
  function onSectionItemClick(item: ISectionChildItem) {
    const [path, hash] = item.href.split('#')
    const isSamePage = path === route.path

    if (isSamePage && hash) {
      emit('close')
    }
  }
</script>
<template>
  <div v-if="activeSection">
    <UiDarkOverlay @click="emit('close')" />
    <div class="absolute left-0 w-full border-t border-divider bg-white">
      <div class="container relative pb-6 pt-8">
        <div
          class="mb-8 grid gap-5"
          :class="activeSection.gridCols === 6 ? 'grid-cols-6' : 'grid-cols-5'"
        >
          <template
            v-for="section in activeSection.items"
            :key="section.label"
          >
            <MainHeaderInfoCard
              v-if="section.isCard"
              :href="section.href"
              :title="section.label"
              :description="section.description"
              :bg-image-src="section.image.src"
              class="col-span-2"
            />
            <div
              v-else
              :class="activeSection.hasBlogBlock ? 'col-span-2' : 'col-span-1'"
            >
              <a
                class="mb-4 block text-bt-blue-dark no-underline text-subtitle-1 hover:underline"
                :href="section.href"
              >
                {{ section.label }}
              </a>
              <div class="flex flex-col gap-3">
                <NuxtLink
                  v-for="item in section.items"
                  :key="item.label"
                  :to="item.href"
                  class="break-words text-bt-blue-dark no-underline text-body-2 hover:underline"
                  external
                  @click="onSectionItemClick(item)"
                >
                  {{ item.label }}
                </NuxtLink>
              </div>
            </div>
            <template v-if="activeSection.hasBlogBlock">
              <MainHeaderInfoCard
                v-for="entry in blogEntries"
                :key="entry.image"
                :href="entry.link.href"
                :title="entry.title"
                :description="entry.content"
                :bg-image-src="entry.image"
                class="col-span-2"
              />
            </template>
          </template>
        </div>
        <div class="flex justify-between border-t border-t-bt-green pt-6">
          <NuxtLink
            :to="globalSettingsStore.headerFlightStatusLink?.href"
            class="flex items-center gap-2 no-underline text-body-2 hover:underline"
            external
            no-prefetch
            :aria-label="globalSettingsStore.headerFlightStatusLink?.text"
          >
            <Icon
              name="common/clipboard-clock"
              :alt="globalSettingsStore.headerFlightStatusLink?.text"
            />
            {{ globalSettingsStore.headerFlightStatusLink?.text }}
          </NuxtLink>
          <NuxtLink
            :to="globalSettingsStore.searchPageLink?.href"
            class="flex items-center gap-2 no-underline text-body-2 hover:underline"
            external
            no-prefetch
            :aria-label="globalSettingsStore.searchPageLink?.text"
          >
            <Icon
              name="common/search"
              :alt="globalSettingsStore.searchPageLink?.text"
            />
            {{ globalSettingsStore.searchPageLink?.text }}
          </NuxtLink>
        </div>
        <button
          class="absolute -bottom-3 left-1/2 -rotate-90 rounded-full bg-bt-green p-1"
          :aria-label="$t('aria.label.close')"
          @click="emit('close')"
        >
          <Icon name="common/chevron-right" />
        </button>
      </div>
    </div>
  </div>
</template>
